import React, { useMemo } from "react";
import {
  MapPinIcon,
  UsersIcon,
  BoltIcon,
  ClockIcon,
  RocketLaunchIcon,
  SparklesIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";

const fieldList = ["location","collaboration","motivation","commitment","energyRhythm","executionPace"];

const Pill = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="group flex items-start gap-2 rounded-xl border border-gray-200/80 bg-white px-3 py-2 shadow-sm hover:shadow transition-all hover:-translate-y-0.5">
      <div className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-neutral-50 grid place-items-center border border-gray-200">
        <Icon className="h-4 w-4 text-gray-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 whitespace-normal break-words">
          {label}
        </div>
        <div className="text-sm text-gray-900 whitespace-normal break-words leading-5">
          {value}
        </div>
      </div>
    </div>
  );
};



const Avatar = ({ name, src }) => {
  const initials = (name || "You")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="relative">
      <span
        className="pointer-events-none absolute -inset-1 rounded-2xl
                   bg-gradient-to-tr from-fuchsia-300/30 via-sky-300/25 to-emerald-300/30
                   blur-lg animate-pulse"
        aria-hidden
      />
      {src ? (
        <img
          src={src}
          alt={name || "Profile"}
          className="relative h-14 w-14 rounded-2xl object-cover ring-1 ring-black/5"
        />
      ) : (
        <div className="relative h-14 w-14 rounded-2xl bg-neutral-100 text-gray-700 grid place-items-center text-base font-semibold ring-1 ring-black/5">
          {initials}
        </div>
      )}
      <span className="absolute -right-1 -bottom-1 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" />
    </div>
  );
};

const Progress = ({ pct }) => (
  <div className="w-full">
    <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
      <span>Profile completeness</span>
      <span>{pct}%</span>
    </div>
    <div className="h-2 rounded-full bg-neutral-100 overflow-hidden ring-1 ring-inset ring-black/5">
      <div
        className="h-full rounded-full bg-gradient-to-r from-fuchsia-500 via-sky-500 to-emerald-500 transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  </div>
);

const ExperienceItem = ({ exp }) => {
  if (!exp?.title && !exp?.summary) return null;
  return (
    <div className="group relative rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
      <div className="absolute left-0 top-0 h-full w-1 rounded-l-xl bg-gradient-to-b from-sky-400 to-emerald-400 opacity-70" />
      <div className="pl-3">
        <div className="flex items-center gap-2">
          <RocketLaunchIcon className="h-4 w-4 text-gray-700" />
          <div className="font-medium text-gray-900">{exp.title}</div>
        </div>
        {exp.role && <div className="text-xs text-gray-500 mt-0.5">{exp.role}</div>}
        {exp.summary && <div className="text-sm text-gray-800 mt-2">{exp.summary}</div>}
        {exp.link && (
          <a
            href={exp.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-700 mt-2"
          >
            <LinkIcon className="h-3.5 w-3.5" />
            View project
          </a>
        )}
      </div>
    </div>
  );
};

const BuilderCard = ({ profile = {}, identity = [], profileImage }) => {
  const {
    name,
    location,
    collaboration,
    motivation,
    commitment,
    energyRhythm,
    executionPace,
  } = profile;

  // Parse experience like before
  let parsedExperience = [];
  if (typeof profile.experience === "string") {
    try {
      parsedExperience = JSON.parse(profile.experience.replace(/^Experience:\s*/, ""));
    } catch (err) {
      console.error("Error parsing experience:", err);
    }
  } else if (Array.isArray(profile.experience)) {
    parsedExperience = profile.experience;
  }

  const filled = useMemo(() => {
    let n = 0;
    if (location) n++;
    if (collaboration) n++;
    if (motivation) n++;
    if (commitment) n++;
    if (energyRhythm) n++;
    if (executionPace) n++;
    return n + (parsedExperience?.length > 0 ? 1 : 0);
  }, [location, collaboration, motivation, commitment, energyRhythm, executionPace, parsedExperience]);
  const total = 7;
  const pct = Math.round((filled / total) * 100);

  return (
    <div className="relative">
      {/* Animated soft border wrapper */}
      <div className="rounded-2xl p-[1px] bg-gradient-to-tr from-fuchsia-200/60 via-sky-200/60 to-emerald-200/60">
        <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <Avatar name={name} src={profileImage} />
              <div>
                <div className="text-base font-semibold text-gray-900 leading-5">{name || "Your Name"}</div>
                {identity?.[0] && (
                  <div className="mt-0.5 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700">
                    <SparklesIcon className="h-3.5 w-3.5" />
                    {identity[0]}
                  </div>
                )}
              </div>
            </div>
            <div className="w-40 hidden sm:block">
              <Progress pct={pct} />
            </div>
          </div>

          {/* Quick facts */}
          <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-2">
            <Pill icon={MapPinIcon} label="Location" value={location} />
            <Pill icon={UsersIcon} label="Collaboration" value={collaboration} />
            <Pill icon={SparklesIcon} label="Motivation" value={motivation} />
            <Pill icon={ClockIcon} label="Weekly Commitment" value={commitment} />
            <Pill icon={BoltIcon} label="Most Productive Time" value={energyRhythm} />
            <Pill icon={RocketLaunchIcon} label="Execution Style" value={executionPace} />
          </div>

          {/* Experience */}
          {parsedExperience?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs font-bold uppercase text-gray-400 mb-2">Experience</div>
              <div className="space-y-2">
                {parsedExperience.slice(0, 3).map((exp, i) => (
                  <ExperienceItem key={i} exp={exp} />
                ))}
                {parsedExperience.length > 3 && (
                  <div className="text-xs text-gray-500 mt-1">
                    +{parsedExperience.length - 3} more project{parsedExperience.length - 3 > 1 ? "s" : ""}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Codi’s Read */}
          {identity?.[1] && (
            <div className="mt-5">
              <div className="text-xs font-bold uppercase text-gray-400 mb-2">Codi’s Read</div>
              <div className="relative rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-indigo-900 shadow-sm">
                <span className="absolute left-3 top-3 h-1.5 w-1.5 rounded-full bg-indigo-400 animate-pulse" />
                <div className="pl-4 text-sm font-medium">{identity[1]}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile progress (shown if hidden on desktop) */}
      <div className="sm:hidden mt-3">
        <Progress pct={pct} />
      </div>
    </div>
  );
};

export default BuilderCard;
